import { cn } from '@/lib/utils'
import NumberFlow, { NumberFlowProps } from '@number-flow/react'
import React from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/HybridTooltip'
import type { TooltipProps } from '@radix-ui/react-tooltip'

type StatProps = React.PropsWithChildren<{
	className?: string
	src?: string
	label: string
	value: string | number | bigint
}>

const StatContext = React.createContext<Omit<StatProps, 'children' | 'className'>>({} as any)
const useStatContext = () => React.useContext(StatContext)

function Stat({ src, label, value, className, children }: StatProps) {
	return (
		<div className={cn('flex flex-col justify-center items-center', className)}>
			<StatContext.Provider value={{ label, value, src }}>{children}</StatContext.Provider>
		</div>
	)
}

function StatImage(props: Omit<React.HtmlHTMLAttributes<HTMLImageElement>, 'src' | 'alt'>) {
	const { src, label } = useStatContext()

	return <img src={src} alt={label} width={60} height={60} {...props} />
}

function StatLabel({ className, ...props }: React.HtmlHTMLAttributes<HTMLHeadingElement>) {
	const { label } = useStatContext()

	return (
		<h2 className={cn('text-[1.75rem] text-green-10 uppercase leading-none mt-4 mb-1', className)} {...props}>
			{label}
		</h2>
	)
}

function StatTooltip({
	content,
	className,
	children,
	...props
}: TooltipProps & { content: string; className?: string }) {
	return (
		<TooltipProvider {...props}>
			<Tooltip modal>
				<TooltipTrigger>{children}</TooltipTrigger>
				<TooltipContent align="end" alignOffset={-115} className={className}>
					{content}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

function StatNumber({ className, ...props }: Omit<NumberFlowProps, 'value'>) {
	const { value } = useStatContext()

	return (
		<NumberFlow
			className={cn('text-[2rem] font-bold text-neutral text-center !leading-none', className)}
			continuous
			isolate
			format={{ maximumFractionDigits: 0 }}
			transformTiming={{ duration: 650 }}
			value={Number(value)}
			{...props}
		/>
	)
}

Stat.Image = StatImage
Stat.Label = StatLabel
Stat.Tooltip = StatTooltip
Stat.Number = StatNumber

export { Stat, type StatProps }
